import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { Logo, LanguageSwitcher } from "@/components/shared";
import api from "@/lib/api";
import {
  LayoutDashboard, Wheat, ShoppingBasket, Factory, LifeBuoy, GitMerge, Receipt,
  Truck, Recycle, MessageSquareWarning, FileText, Presentation, ShieldCheck,
  Users, Inbox, ScrollText, Settings, LogOut, Menu, X, BarChart3, CheckCheck, Sprout, FileBarChart,
  Flower2, Bell, Hexagon,
} from "lucide-react";

const ICONS = {
  overview: LayoutDashboard, crops: Wheat, demand: ShoppingBasket, processing: Factory,
  rescue: LifeBuoy, matching: GitMerge, settlements: Receipt, logistics: Truck,
  recovery: Recycle, disputes: MessageSquareWarning, proposals: FileText,
  investor: Presentation, governance: ShieldCheck, users: Users, leads: Inbox,
  audit: ScrollText, settings: Settings, impact: BarChart3,
  approvals: CheckCheck, soil: Sprout, brief: FileBarChart,
  pollination: Flower2, alerts: Bell, habitat: Sprout, honey: Hexagon,
};

function navFor(role, flags = {}) {
  const base = [["", "overview"]];
  const soil = flags.soil_planning ? [["soil", "soil"]] : [];
  const bees = [];
  if (flags.pollination_matching) bees.push(["pollination", "pollination"]);
  if (flags.treatment_alerts) bees.push(["treatment-alerts", "alerts"]);
  if (flags.habitat_actions) bees.push(["habitat", "habitat"]);
  if (flags.honey_collection) bees.push(["honey", "honey"]);
  const map = {
    farmer: [...base, ["crops", "myCrops"], ["settlements", "settlements"], ["logistics", "logistics"], ["disputes", "disputes"], ["recovery", "recovery"], ...bees, ...soil],
    buyer: [...base, ["demand", "myDemand"], ["disputes", "disputes"], ...(flags.honey_collection ? [["honey", "honey"]] : [])],
    processor: [...base, ["processing", "capacity"], ["recovery", "recovery"]],
    beekeeper: [...base, ...bees],
    coordinator: [...base, ["rescue", "rescue"], ["crops", "myCrops"], ["demand", "myDemand"], ["processing", "capacity"], ["matching", "matching"], ["settlements", "settlements"], ["logistics", "logistics"], ["recovery", "recovery"], ["disputes", "disputes"], ["approvals", "approvals"], ...bees, ["proposals", "proposals"], ["investor-brief", "brief"], ["governance", "governance"], ["impact", "impact"], ...soil],
    investor: [...base, ["investor", "investor"], ["investor-brief", "brief"], ["proposals", "proposals"]],
  };
  const admin = [...base, ["rescue", "rescue"], ["crops", "myCrops"], ["demand", "myDemand"], ["processing", "capacity"], ["matching", "matching"], ["settlements", "settlements"], ["logistics", "logistics"], ["recovery", "recovery"], ["disputes", "disputes"], ["approvals", "approvals"], ...bees, ["proposals", "proposals"], ["investor", "investor"], ["investor-brief", "brief"], ["governance", "governance"], ["impact", "impact"], ...soil, ["leads", "leads"], ["users", "users"], ["audit", "audit"], ["settings", "settings"]];
  if (role === "admin" || role === "super_admin") return admin;
  return map[role] || base;
}

export default function DashboardLayout() {
  const { t } = useT();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [flags, setFlags] = useState({});

  useEffect(() => {
    api.get("/feature-flags").then((r) => setFlags(r.data)).catch(() => {});
  }, []);

  if (!user) return null;
  const items = navFor(user.role, flags);

  const doLogout = async () => { await logout(); nav("/"); };

  const Sidebar = () => (
    <div className="flex h-full flex-col">
      <div className="border-b border-subtle p-4"><Logo /></div>
      <div className="border-b border-subtle px-4 py-3">
        <div className="text-xs uppercase tracking-wide text-mutedtext">{t("dashboard.welcome")}</div>
        <div className="truncate text-sm font-semibold text-charcoal">{user.name}</div>
        <div className="mt-1 inline-block rounded bg-forest/10 px-2 py-0.5 text-xs font-medium text-forest">{t(`roles.${user.role}`)}</div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Dashboard">
        {items.map(([path, key]) => {
          const Icon = ICONS[key] || ICONS[path] || LayoutDashboard;
          const to = path ? `/app/${path}` : "/app";
          const active = loc.pathname === to;
          return (
            <Link key={key} to={to} onClick={() => setOpen(false)} data-testid={`side-${path || "overview"}`}
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-forest text-white" : "text-charcoal hover:bg-forest/5"}`}>
              <Icon className="h-4 w-4" aria-hidden="true" /> {t(`dashboard.${key}`)}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-subtle p-3">
        <LanguageSwitcher />
        <button onClick={doLogout} data-testid="logout-btn" className="mt-3 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-crittext hover:bg-critbg">
          <LogOut className="h-4 w-4" aria-hidden="true" /> {t("dashboard.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-offwhite">
      <div className="lg:flex">
        <aside className="hidden w-64 shrink-0 border-r border-subtle bg-white lg:block lg:sticky lg:top-0 lg:h-screen">
          <Sidebar />
        </aside>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between border-b border-subtle bg-white px-4 py-3 lg:hidden">
            <Logo />
            <button onClick={() => setOpen(true)} aria-label="Menu" data-testid="dash-menu-btn"><Menu className="h-6 w-6" /></button>
          </div>
          {open && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
              <div className="absolute left-0 top-0 h-full w-72 bg-white">
                <button onClick={() => setOpen(false)} className="absolute right-3 top-3" aria-label="Close"><X className="h-5 w-5" /></button>
                <Sidebar />
              </div>
            </div>
          )}
          <div className="mx-auto max-w-6xl p-4 sm:p-6">
            <Outlet context={{ flags }} />
          </div>
        </div>
      </div>
    </div>
  );
}
